import { useContext, useEffect, useRef, useState } from 'react';
import { QueryKey, useQuery } from '@tanstack/react-query';

import { DataPoint } from '../../types';
import { Dasloop } from '../../types/Device';
import {
  WebSocketCoordinates,
  WebSocketDasloopDataPoint,
} from '../../types/Websocket';

import { getProjectDasloops } from '../../apis/DasloopApi';

import { PubSubContext } from '../../utils/PubSub';

type UseProjectDasloopMapParams = {
  queryKey: QueryKey;
  projectId: string | null | undefined;
  refetchInterval?: number;
  refetchOnWindowFocus?: boolean;
  enabled?: boolean;
  indexBy?: 'id' | 'dasId';
};

type DasloopMap = { [id: string]: Dasloop | undefined };

export const useProjectDasloopMap = ({
  queryKey,
  projectId,
  refetchInterval,
  refetchOnWindowFocus,
  enabled = true,
  indexBy = 'id',
}: UseProjectDasloopMapParams) => {
  const dataPointQueueRef = useRef<WebSocketDasloopDataPoint[]>([]);
  const coordinatesQueueRef = useRef<WebSocketCoordinates[]>([]);
  const pubSub = useContext(PubSubContext);
  const [state, setState] = useState<{
    isFetching: boolean;
    map: DasloopMap;
  }>({
    isFetching: true,
    map: {},
  });
  const { data, isFetching, ...others } = useQuery<DasloopMap>(
    queryKey.concat(indexBy),
    async () => {
      let localDevices: Dasloop[] = [];
      const load = async (nextCursor?: string | undefined) => {
        const res = await getProjectDasloops({
          projectId: projectId as string,
          params: {
            nextCursor,
          },
        });
        localDevices = localDevices.concat(res.data.data);
        if (res.data.paging.nextCursor) {
          await load(res.data.paging.nextCursor);
        }
      };
      await load();
      return localDevices.reduce((prev, curr) => {
        if (indexBy === 'id') {
          prev[curr.id] = curr;
        } else {
          prev[curr.dasId] = curr;
        }
        return prev;
      }, {});
    },
    {
      enabled: !!projectId && enabled,
      refetchInterval,
      refetchOnWindowFocus,
    },
  );

  useEffect(() => {
    const dataPointHandler = (datapointData: WebSocketDasloopDataPoint) => {
      if (projectId === datapointData.endpoint.metadata['dsm/projectId']) {
        dataPointQueueRef.current.push(datapointData);
      }
    };

    const coordinatesHandler = (coordinatesData: WebSocketCoordinates) => {
      if (projectId === coordinatesData.endpoint.metadata['dsm/projectId']) {
        coordinatesQueueRef.current.push(coordinatesData);
      }
    };

    pubSub?.subscribe('dasloop:data-point-received', dataPointHandler);
    pubSub?.subscribe('coordinates-updated', coordinatesHandler);
    return () => {
      pubSub?.unsubscribe('dasloop:data-point-received', dataPointHandler);
      pubSub?.unsubscribe('coordinates-updated', coordinatesHandler);
    };
  }, [pubSub, projectId, indexBy]);

  useEffect(() => {
    setState({
      map: data ?? {},
      isFetching,
    });
  }, [data, isFetching]);

  useEffect(() => {
    const dataPointQueueTimer = setInterval(() => {
      const dataPoints = [...dataPointQueueRef.current];
      if (dataPoints.length > 0) {
        setState((origin) => {
          let currentDeviceMap = { ...origin.map };
          dataPoints.forEach((datapointData) => {
            let index = '';
            if (indexBy === 'id') {
              index = datapointData.endpoint.id;
            } else if (indexBy === 'dasId') {
              index = datapointData.endpoint.dasId;
            }
            const device = currentDeviceMap[index];
            const updateDataPoint: { [key: string]: DataPoint<any> } =
              Object.entries(datapointData.dataPoint).reduce(
                (prev, [key, value]) => {
                  if (value !== undefined || value !== null) {
                    prev[key] = {
                      timestamp: datapointData.timestamp,
                      value,
                      stale: false,
                    };
                  }
                  return prev;
                },
                {},
              );
            if (device) {
              currentDeviceMap = {
                ...currentDeviceMap,
                [index]: {
                  ...device,
                  shadow: {
                    dataPoint: {
                      ...device.shadow.dataPoint,
                      ...updateDataPoint,
                    },
                  },
                },
              };
            }
          });
          dataPointQueueRef.current = [];
          return {
            ...origin,
            map: currentDeviceMap,
          };
        });
      }
    }, 1000);

    const coordinatesQueueTimer = setInterval(() => {
      const coords = [...coordinatesQueueRef.current];
      if (coords.length > 0) {
        setState((origin) => {
          let currentDeviceMap = { ...origin.map };
          coords.forEach((coordinatesData) => {
            let index = '';
            if (indexBy === 'id') {
              index = coordinatesData.endpoint.id;
            } else if (indexBy === 'dasId') {
              index = coordinatesData.endpoint.dasId;
            }

            const device = currentDeviceMap[index];

            if (device) {
              const updateDataPoint: { [key: string]: DataPoint<any> } =
                Object.entries(
                  coordinatesData.endpoint.shadow.dataPoint,
                ).reduce((prev, [key, value]) => {
                  if (value !== undefined || value !== null) {
                    prev[key] = value;
                  }
                  return prev;
                }, {});

              device.shadow.dataPoint.beacons =
                coordinatesData.endpoint.shadow.dataPoint.beacons ??
                device.shadow.dataPoint.beacons;
              currentDeviceMap = {
                ...currentDeviceMap,
                [index]: {
                  ...device,
                  shadow: {
                    dataPoint: {
                      ...device.shadow.dataPoint,
                      ...updateDataPoint,
                    },
                  },
                  coordinates: coordinatesData.coordinates,
                  coordinatesSource: coordinatesData.source,
                  lastCoordinatesUpdatedAt: coordinatesData.timestamp,
                },
              };
            }
          });
          coordinatesQueueRef.current = [];
          return {
            ...origin,
            map: currentDeviceMap,
          };
        });
      }
    }, 1000);

    return () => {
      clearInterval(dataPointQueueTimer);
      clearInterval(coordinatesQueueTimer);
    };
  }, []);

  return {
    data: state.map,
    isFetching: state.isFetching,
    ...others,
  };
};
